import React, { useState, useEffect } from 'react';
import { User } from '../types';
import SettingsCard from './settings/SettingsCard';
import SubscriptionSection from './settings/SubscriptionSection';
import ProfileSection from './settings/ProfileSection';
import PreferencesSection, { UserPreferences } from './settings/PreferencesSection';
import SecuritySection from './settings/SecuritySection';
import StatsSection, { UserStats } from './settings/StatsSection';
import SettingsErrorBoundary from './settings/SettingsErrorBoundary';
import { updateUserProfile, ProfileData } from '../services/profileService';
import { getUserPreferences, updateUserPreferences, defaultPreferences } from '../services/preferencesService';
import { deleteUserAccount } from '../services/accountService';
import { getCachedUserStatistics, generateMockStats, refreshUserStatistics } from '../services/statisticsService';
import { useAuth } from '../AuthContext';
import { Settings, User as UserIcon, CreditCard, SlidersHorizontal, Shield, BarChart3, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface UserSettingsPageProps {
  user: User;
  onNavigate?: (section: string) => void;
}

const UserSettingsPage: React.FC<UserSettingsPageProps> = ({ user, onNavigate }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);
  const [preferencesLoading, setPreferencesLoading] = useState(true);
  const [stats, setStats] = useState<UserStats>({
    totalStudyTime: 0,
    completedModules: 0,
    currentStreak: 0,
    longestStreak: 0,
    averageSessionTime: 0,
    studyHistory: []
  });
  const [statsLoading, setStatsLoading] = useState(true);

  // Load user preferences and stats on component mount
  useEffect(() => {
    const loadData = async () => {
      if (currentUser) {
        try {
          // Load preferences
          const userPrefs = await getUserPreferences(currentUser.uid);
          setPreferences(userPrefs);
        } catch (error) {
          console.error('Error loading preferences:', error);
        } finally {
          setPreferencesLoading(false);
        }

        try {
          // Always try to load real stats first
          console.log('🔄 Loading user statistics...');
          const userStats = await getCachedUserStatistics(currentUser.uid);
          setStats(userStats);
          console.log('✅ Statistics loaded successfully');
        } catch (error) {
          console.error('❌ Error loading stats, using fallback:', error);
          // Only use mock data as absolute last resort
          const mockStats = generateMockStats();
          setStats(mockStats);
        } finally {
          setStatsLoading(false);
        }
      }
    };

    loadData();
  }, [currentUser]);

  const handleProfileUpdate = async (data: Partial<ProfileData>) => {
    try {
      await updateUserProfile(data);
      setNotification({ type: 'success', message: 'Profil byl úspěšně aktualizován' });
      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      console.error('Profile update error:', error);
      setNotification({
        type: 'error',
        message: error instanceof Error ? error.message : 'Nepodařilo se aktualizovat profil'
      });
      setTimeout(() => setNotification(null), 5000);
      throw error; // Re-throw to let ProfileSection handle it
    }
  };

  const handlePreferencesUpdate = async (data: Partial<UserPreferences>) => {
    try {
      await updateUserPreferences(data);
      // Update local state immediately for better UX
      setPreferences(prev => ({
        ...prev,
        ...data,
        studySettings: {
          ...prev.studySettings,
          ...(data.studySettings || {})
        }
      }));
      setNotification({ type: 'success', message: 'Předvolby byly úspěšně aktualizovány' });
      setTimeout(() => setNotification(null), 3000);
    } catch (error) {
      console.error('Preferences update error:', error);
      setNotification({
        type: 'error',
        message: error instanceof Error ? error.message : 'Nepodařilo se aktualizovat předvolby'
      });
      setTimeout(() => setNotification(null), 5000);
      throw error;
    }
  };

  const handleAccountDelete = async () => {
    try {
      await deleteUserAccount();
      // User will be automatically logged out and redirected to login
      setNotification({ type: 'success', message: 'Účet byl úspěšně smazán' });
    } catch (error) {
      console.error('Account deletion error:', error);
      setNotification({
        type: 'error',
        message: error instanceof Error ? error.message : 'Nepodařilo se smazat účet'
      });
      setTimeout(() => setNotification(null), 5000);
      throw error; // Re-throw to let SecuritySection handle it
    }
  };
  return (
    <SettingsErrorBoundary>
      <div className="flex flex-col h-full min-h-0">
        <div
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
            {/* Header */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-2">
                <Settings className="w-9 h-9 text-primary" strokeWidth={2.5} />
                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
                  Nastavení
                </h1>
              </div>
              <p className="text-base text-gray-500 dark:text-gray-400 ml-[52px]">
                Spravujte svůj profil a přizpůsobte si aplikaci
              </p>
            </div>

            {/* Global Notification */}
            {notification && (
              <div className={`mb-6 p-4 rounded-2xl border-2 font-medium transition-all duration-300 ${notification.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300'
                }`}>
                {notification.message}
              </div>
            )}

            {/* Settings Sections */}
            <div className="space-y-6">
              {/* Profile Section */}
              <SettingsCard
                title="Profil"
                description="Upravte své osobní údaje"
                icon={<UserIcon className="w-6 h-6" strokeWidth={2.5} />}
              >
                <ProfileSection user={user} onProfileUpdate={handleProfileUpdate} />
              </SettingsCard>

              {/* Subscription Section */}
              <SettingsCard
                title="Můj Plán"
                description="Spravujte svůj přístup a fakturaci"
                icon={<CreditCard className="w-6 h-6" strokeWidth={2.5} />}
              >
                <SubscriptionSection onNavigate={onNavigate} />
              </SettingsCard>

              {/* Preferences Section */}
              <SettingsCard
                title="Předvolby"
                description="Přizpůsobte si aplikaci podle svých potřeb"
                icon={<SlidersHorizontal className="w-6 h-6" strokeWidth={2.5} />}
              >
                {preferencesLoading ? (
                  <div className="text-gray-500 dark:text-gray-400 text-center py-8">
                    Načítám předvolby...
                  </div>
                ) : (
                  <PreferencesSection
                    preferences={preferences}
                    onPreferencesUpdate={handlePreferencesUpdate}
                  />
                )}
              </SettingsCard>

              {/* Security Section */}
              <SettingsCard
                title="Zabezpečení"
                description="Spravujte zabezpečení svého účtu"
                icon={<Shield className="w-6 h-6" strokeWidth={2.5} />}
              >
                <SecuritySection user={user} onAccountDelete={handleAccountDelete} />
              </SettingsCard>

              {/* Statistics Section */}
              <SettingsCard
                title="Statistiky"
                description="Sledujte svůj pokrok a úspěchy"
                icon={<BarChart3 className="w-6 h-6" strokeWidth={2.5} />}
              >
                <StatsSection
                  stats={stats}
                  isLoading={statsLoading}
                  onRetry={async () => {
                    if (currentUser) {
                      setStatsLoading(true);
                      try {
                        const userStats = await refreshUserStatistics(currentUser.uid);
                        setStats(userStats);
                      } catch (error) {
                        console.error('Error reloading stats:', error);
                        const mockStats = generateMockStats();
                        setStats(mockStats);
                      } finally {
                        setStatsLoading(false);
                      }
                    }
                  }}
                />
              </SettingsCard>

              {/* Feedback Link */}
              <button
                onClick={() => navigate('/feedback')}
                className="w-full bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl border-2 border-indigo-200 dark:border-indigo-800 p-6 text-left hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors group"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-indigo-100 dark:bg-indigo-900/40 rounded-2xl p-3">
                    <MessageSquare className="w-6 h-6 text-indigo-600 dark:text-indigo-400" strokeWidth={2.5} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                      Zpětná vazba
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Pomoz nám vylepšit aplikaci — tvůj názor je pro nás důležitý
                    </p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 group-hover:text-indigo-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </SettingsErrorBoundary>
  );
};

export default UserSettingsPage;